'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-accent text-accent-foreground shadow-[0_8px_24px_-12px_var(--color-accent)] hover:opacity-90',
        secondary:
          'bg-surface-muted text-foreground border border-border hover:bg-accent-soft',
        ghost:
          'text-foreground-muted hover:bg-accent-soft hover:text-foreground',
        // Destructive actions carry more visual weight than anything else on
        // the screen: bolder label, a danger-tinted lift, and a danger focus
        // ring instead of the neutral one. A moderator must never mistake the
        // ban control for the row of neutral quick-actions beside it.
        danger:
          'bg-danger-solid text-white font-semibold shadow-[0_10px_28px_-14px_var(--color-danger)] hover:opacity-90 focus-visible:ring-danger',
        // Same alarm, less ink — for destructive triggers that sit inside a
        // dense toolbar or drawer where a fully filled button would shout over
        // the content it belongs to. Fills on hover so the commitment is
        // obvious the moment the pointer lands.
        dangerSoft:
          'border border-danger/50 bg-danger/10 text-danger font-semibold hover:border-danger-solid hover:bg-danger-solid hover:text-white focus-visible:ring-danger',
        outline:
          'border border-border bg-transparent text-foreground hover:bg-accent-soft',
      },
      // Every size grows on a coarse pointer. The panel's dense sizes (`sm`
      // 32px, `icon-sm` 28px) are deliberate with a mouse and below the 44px
      // bar with a finger — and `icon-sm` is what row-action buttons use, so
      // it is exactly the size an operator taps most on a phone.
      size: {
        sm: 'h-8 touch:h-11 px-3 touch:px-4 text-xs',
        md: 'h-9 touch:h-11 px-4',
        lg: 'h-10 touch:h-11 px-5',
        icon: 'h-9 w-9 touch:h-11 touch:w-11 p-0',
        'icon-sm': 'h-7 w-7 touch:h-11 touch:w-11 p-0',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
